/**
 * Simplified XR Service that uses the existing splatv-module renderer
 */

import type { CameraState } from '@/types/camera'
// @ts-ignore
import { ARTouchController } from '@/lib/splatv/ar-touch-controller'
import { XRInputHandler } from './xr-input-handler'
import { XRGestureHandler } from './xr-gesture-handler'

export interface XRServiceOptions {
  onSessionStart?: () => void
  onSessionEnd?: () => void
}

export class XRServiceSimple {
  private session: XRSession | null = null
  private referenceSpace: XRReferenceSpace | null = null
  private renderer: any = null // splatv-module instance
  private rendererParent: any = null // SplatRenderer instance
  private gl: WebGL2RenderingContext | null = null
  private options: XRServiceOptions
  private touchController: ARTouchController | null = null
  private xrInputHandler: XRInputHandler | null = null
  private xrGestureHandler: XRGestureHandler | null = null
  private sessionType: 'vr' | 'ar' | null = null
  
  constructor(options: XRServiceOptions = {}) {
    this.options = options
  }
  
  /**
   * Check if WebXR is supported
   */
  async checkSupport(): Promise<{ ar: boolean; vr: boolean }> {
    console.log('XRServiceSimple: checkSupport called')
    
    if (!navigator.xr) {
      console.log('navigator.xr not available')
      return { ar: false, vr: false }
    }
    
    console.log('navigator.xr available, checking session support...')
    
    const [vr, ar] = await Promise.all([
      navigator.xr.isSessionSupported('immersive-vr'),
      navigator.xr.isSessionSupported('immersive-ar')
    ])
    
    console.log('XR session support:', { ar, vr })
    
    return { ar, vr }
  }
  
  /**
   * Start XR session using existing renderer
   */
  async startSession(
    renderer: any, // splatv-module instance
    sessionType: 'vr' | 'ar' = 'ar',
    defaultCamera?: CameraState
  ): Promise<void> {
    if (this.session) {
      throw new Error('XR session already active')
    }
    
    this.renderer = renderer
    this.sessionType = sessionType
    this.gl = renderer.gl
    
    if (!this.gl) {
      throw new Error('WebGL context not available from renderer')
    }
    
    // Set XR mode flag to prevent renderer from clearing framebuffer
    this.renderer.xrMode = true
    
    // Check if context is XR compatible
    if (!(this.gl as any).xrCompatible) {
      console.warn('WebGL context may not be XR compatible')
    }
    
    const xrSessionType = sessionType === 'ar' ? 'immersive-ar' : 'immersive-vr'
    
    try {
      // Request XR session
      this.session = await navigator.xr!.requestSession(xrSessionType)
      
      // Setup session event handlers
      this.session.addEventListener('end', () => this.handleSessionEnd())
      
      // Create XR WebGL layer using the existing context
      const xrLayer = new XRWebGLLayer(this.session, this.gl)
      
      // Update render state
      await this.session.updateRenderState({
        baseLayer: xrLayer
      })
      
      // Get reference space
      this.referenceSpace = await this.session.requestReferenceSpace('local')
      
      // Apply default camera if provided
      if (defaultCamera) {
        this.applyDefaultCamera(defaultCamera)
      }
      
      // Store the renderer's parent (SplatRenderer) if available
      this.rendererParent = renderer.renderer
      
      // Stop the regular render loop if there's a parent renderer
      if (this.rendererParent && this.rendererParent.stopRenderLoop) {
        this.rendererParent.stopRenderLoop()
      }
      
      // Initialize input handling for AR
      if (sessionType === 'ar') {
        console.log('Initializing XR gesture handler for AR')
        
        // Try the new gesture handler based on XRGestures pattern
        this.xrGestureHandler = new XRGestureHandler(this.renderer)
        this.xrGestureHandler.initialize(this.session)
        
        // Set initial model transform
        const initialTransform = this.xrGestureHandler.getModelTransform()
        console.log('Setting initial model transform:', initialTransform)
        this.renderer.setModelTransform(initialTransform)
        
        console.log('Using XRGestureHandler for AR input')
      }
      
      // Start XR render loop
      this.session.requestAnimationFrame(this.onXRFrame.bind(this))
      
      // Notify session started
      if (this.options.onSessionStart) {
        this.options.onSessionStart()
      }
      
      console.log(`XR session started: ${xrSessionType}`)
      
    } catch (error) {
      console.error('Failed to start XR session:', error)
      throw error
    }
  }
  
  /**
   * XR frame callback - uses existing renderer
   */
  private frameCount = 0
  
  private onXRFrame(_time: number, frame: XRFrame) {
    this.frameCount++
    
    // Log every 30 frames (about once per second at 30fps)
    const shouldLog = this.frameCount % 30 === 0
    
    if (shouldLog) {
      console.log(`XR Frame #${this.frameCount} starting`)
    }
    
    if (!this.session || !this.gl || !this.renderer) {
      console.error('XR Frame: Missing required objects', {
        session: !!this.session,
        gl: !!this.gl,
        renderer: !!this.renderer
      })
      return
    }
    
    const pose = frame.getViewerPose(this.referenceSpace!)
    if (!pose) {
      if (shouldLog) console.warn('No pose available')
      this.session.requestAnimationFrame(this.onXRFrame.bind(this))
      return
    }
    
    if (shouldLog) {
      console.log('XR Frame rendering, views:', pose.views.length)
    }
    
    const glLayer = this.session.renderState.baseLayer!
    
    // Store original framebuffer
    const originalFramebuffer = this.gl.getParameter(this.gl.FRAMEBUFFER_BINDING)
    
    // Verify framebuffer
    if (shouldLog) {
      console.log('Current framebuffer:', originalFramebuffer, 'XR framebuffer:', glLayer.framebuffer)
    }
    
    // Bind XR framebuffer
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, glLayer.framebuffer)
    
    // Verify binding
    const boundFramebuffer = this.gl.getParameter(this.gl.FRAMEBUFFER_BINDING)
    if (boundFramebuffer !== glLayer.framebuffer) {
      console.error('Failed to bind XR framebuffer!')
    }
    
    // Clear with transparent for AR camera passthrough
    this.gl.clearColor(0, 0, 0, 0)
    this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT)
    
    // Render for each eye
    for (const view of pose.views) {
      const viewport = glLayer.getViewport(view)
      if (!viewport) {
        console.error('No viewport for view')
        continue
      }
      
      // Set viewport for this eye
      this.gl.viewport(viewport.x, viewport.y, viewport.width, viewport.height)
      
      if (shouldLog) {
        console.log(`Rendering eye ${view.eye}:`, {
          viewport: `${viewport.x},${viewport.y} ${viewport.width}x${viewport.height}`
        })
      }
      
      // Now enable splatv rendering
      // Update renderer matrices
      this.renderer.projMatrix = view.projectionMatrix
      this.renderer.viewMatrix = view.transform.inverse.matrix
      
      // Update focal length from projection matrix
      const fx = (view.projectionMatrix[0] * viewport.width) / 2
      const fy = (view.projectionMatrix[5] * viewport.height) / 2
      this.renderer.fx = fx
      this.renderer.fy = fy
      
      // CRITICAL: Set viewport dimensions for XR
      // The shader uses these values for splat sizing
      this.renderer.viewportWidth = viewport.width
      this.renderer.viewportHeight = viewport.height
      
      // CRITICAL: Trigger depth sorting with new matrices
      // Without this, depthIndex remains stale and nothing renders
      if (this.renderer.updateViewProjection) {
        this.renderer.updateViewProjection()
      }
      
      // Log renderer state before render
      if (shouldLog) {
        console.log(`Calling renderer.render() for eye ${view.eye}:`, {
          vertexCount: this.renderer.vertexCount,
          depthIndex: this.renderer.depthIndex ? 'present' : 'missing',
          texture: this.renderer.texture ? 'present' : 'missing',
          initialized: this.renderer.initialized,
          xrMode: this.renderer.xrMode
        })
      }
      
      // Ensure XR framebuffer is still bound (splatv might change it)
      const currentFB = this.gl.getParameter(this.gl.FRAMEBUFFER_BINDING)
      if (currentFB !== glLayer.framebuffer) {
        console.warn('Framebuffer changed! Re-binding XR framebuffer')
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, glLayer.framebuffer)
      }
      
      try {
        this.renderer.render()
        
        // Check if framebuffer was changed by render()
        const afterRenderFB = this.gl.getParameter(this.gl.FRAMEBUFFER_BINDING)
        if (afterRenderFB !== glLayer.framebuffer) {
          console.error('render() changed framebuffer!', afterRenderFB, 'expected:', glLayer.framebuffer)
        }
      } catch (err) {
        console.error('Error rendering splats:', err)
        // Fallback to debug box on error
        this.drawDebugBox()
      }
      
      // Check for GL errors
      const error = this.gl.getError()
      if (error !== this.gl.NO_ERROR) {
        console.error('GL Error after render:', error)
      }
    }
    
    // Process XR gestures
    if (this.xrGestureHandler && this.sessionType === 'ar' && this.referenceSpace) {
      this.xrGestureHandler.update(frame, this.referenceSpace)
    }
    
    // Force GPU to execute commands
    this.gl.flush()
    
    // Request next frame
    this.session.requestAnimationFrame(this.onXRFrame.bind(this))
  }
  
  
  /**
   * Draw a simple red box for debugging
   */
  private drawDebugBox() {
    if (!this.gl) return
    
    // Only log occasionally
    if (this.frameCount % 30 === 0) {
      console.log('Drawing debug box')
    }
    
    // Save state
    const currentProgram = this.gl.getParameter(this.gl.CURRENT_PROGRAM)
    
    // Create simple shader if not exists
    if (!this.debugProgram) {
      this.createDebugProgram()
    }
    
    if (!this.debugProgram) {
      console.error('Failed to create debug program')
      return
    }
    
    // Use debug program
    this.gl.useProgram(this.debugProgram)
    
    // Draw red box
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.debugBuffer!)
    const posLoc = this.gl.getAttribLocation(this.debugProgram, 'position')
    this.gl.enableVertexAttribArray(posLoc)
    this.gl.vertexAttribPointer(posLoc, 2, this.gl.FLOAT, false, 0, 0)
    
    // Disable depth test and enable blending for visibility
    this.gl.disable(this.gl.DEPTH_TEST)
    this.gl.enable(this.gl.BLEND)
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
    
    this.gl.drawArrays(this.gl.TRIANGLE_FAN, 0, 4)
    
    // Restore state
    this.gl.enable(this.gl.DEPTH_TEST)
    this.gl.disable(this.gl.BLEND)
    this.gl.useProgram(currentProgram)
  }
  
  private debugProgram: WebGLProgram | null = null
  private debugBuffer: WebGLBuffer | null = null
  
  private createDebugProgram() {
    if (!this.gl) return
    
    const vs = `
      attribute vec2 position;
      void main() {
        gl_Position = vec4(position, 0.5, 1.0);
      }
    `
    
    const fs = `
      precision mediump float;
      void main() {
        gl_FragColor = vec4(1.0, 1.0, 1.0, 1.0); // White, fully opaque
      }
    `
    
    const vertShader = this.gl.createShader(this.gl.VERTEX_SHADER)!
    this.gl.shaderSource(vertShader, vs)
    this.gl.compileShader(vertShader)
    
    const fragShader = this.gl.createShader(this.gl.FRAGMENT_SHADER)!
    this.gl.shaderSource(fragShader, fs)
    this.gl.compileShader(fragShader)
    
    this.debugProgram = this.gl.createProgram()!
    this.gl.attachShader(this.debugProgram, vertShader)
    this.gl.attachShader(this.debugProgram, fragShader)
    this.gl.linkProgram(this.debugProgram)
    
    // Create buffer - make it bigger and centered
    const vertices = new Float32Array([
      -0.3, -0.3,
       0.3, -0.3,
       0.3,  0.3,
      -0.3,  0.3
    ])
    
    this.debugBuffer = this.gl.createBuffer()
    this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.debugBuffer)
    this.gl.bufferData(this.gl.ARRAY_BUFFER, vertices, this.gl.STATIC_DRAW)
  }
  
  /**
   * Apply default camera transform
   */
  private applyDefaultCamera(camera: CameraState) {
    if (!this.renderer) return
    
    // Convert camera state to view matrix
    // This would need proper implementation based on camera format
    console.log('Applying default camera:', camera)
    
    // For now, just log it
    // In real implementation, would modify the referenceSpace or apply offset
  }
  
  
  /**
   * End XR session
   */
  async endSession(): Promise<void> {
    if (this.session) {
      await this.session.end()
    }
  }
  
  /**
   * Handle session end
   */
  private handleSessionEnd() {
    // Clean up input handlers
    if (this.touchController) {
      this.touchController.disable()
      this.touchController = null
    }
    
    if (this.xrInputHandler) {
      this.xrInputHandler.dispose()
      this.xrInputHandler = null
    }
    
    if (this.xrGestureHandler) {
      this.xrGestureHandler.dispose()
      this.xrGestureHandler = null
    }
    
    // Clean up
    if (this.debugProgram && this.gl) {
      this.gl.deleteProgram(this.debugProgram)
      this.debugProgram = null
    }
    if (this.debugBuffer && this.gl) {
      this.gl.deleteBuffer(this.debugBuffer)
      this.debugBuffer = null
    }
    
    // Clear XR mode flag
    if (this.renderer) {
      this.renderer.xrMode = false
    }
    
    this.session = null
    this.referenceSpace = null
    this.gl = null
    this.sessionType = null
    
    // Restart regular render loop
    if (this.rendererParent && this.rendererParent.restartRenderLoop) {
      this.rendererParent.restartRenderLoop()
    }
    
    // Notify session ended
    if (this.options.onSessionEnd) {
      this.options.onSessionEnd()
    }
    
    console.log('XR session ended')
  }
  
  /**
   * Check if session is active
   */
  isActive(): boolean {
    return this.session !== null
  }
}